"use client";

import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { MinusIcon } from "lucide-react";
import { cn } from "@/lib/utils";

function InputOTP({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
}) {
  return (
    <OTPInput
      data-slot="input-otp"
      containerClassName={cn(
        "flex items-center gap-2 has-disabled:opacity-50",
        containerClassName
      )}
      className={cn("disabled:cursor-not-allowed", className)}
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-otp-group"
      className={cn("flex items-center gap-1", className)}
      {...props}
    />
  );
}

interface OTPContextType {
  slots?: Array<{
    char?: string;
    hasFakeCaret?: boolean;
    isActive?: boolean;
  }>;
  value?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  setActiveIndex?: (index: number) => void;
}

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  index: number;
}) {
  const inputOTPContext = React.useContext(
    OTPInputContext as unknown as React.Context<OTPContextType | undefined>
  ) as OTPContextType | undefined;
  const slot = inputOTPContext?.slots
    ? inputOTPContext.slots[index]
    : undefined;
  const char = (slot?.char ?? inputOTPContext?.value?.[index]) as
    | string
    | undefined;
  const hasFakeCaret = slot?.hasFakeCaret;
  const isActive = slot?.isActive;
  const handleClick = () => {
    inputOTPContext?.inputRef?.current?.focus?.();
    inputOTPContext?.setActiveIndex?.(index);
  };

  return (
    <div
      onClick={handleClick}
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(
        "relative flex h-12 w-12 items-center justify-center rounded-md border text-center text-xl select-none",
        "border-[#dde3e9] bg-white text-[#101828] data-[active=true]:ring-2 data-[active=true]:ring-[#12301b]",
        className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px bg-[#12301b] animate-pulse" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({ ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      <MinusIcon />
    </div>
  );
}

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
